import { FluidMenuItem, FluidMenuState } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import {
  Nav,
  NavButton,
  NavItem,
  NavSubmenu,
  NavSubmenuBtn,
} from '../../../common/fluid-navigation.components';
import {
  MEGA_MENU_COLUMN_LENGTH,
  MEGA_MENU_MAX_COLUMNS,
  MEGA_MENU_MIN_COLUMNS,
} from '../../../constants/constants';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../../utils/utils';
import { hasSubmenu } from '../../fluid-tree-menu/utils/fluid-tree-menu.utils';
import { FluidHeader } from '../fluid-header';
import { hasHeaderSubmenu, hasMegaMenu } from '../utils/fluid-header.utils';
import { HeaderProps } from './fluid-header.components';

const megaMenuMinColumns = 2; // Minimum recommended # of mega menu columns
const megaMenuMaxColumns = 5; // Maximum recommended # of mega menu columns
const megaMenuColumnLength = 8; // Default # of links within "unstructured" mega menu column
const megaMenuMaxColumnLength = 14; // Maximum # of links within "unstructured" mega menu column

export interface HeaderNavProps extends HeaderProps {
  headerState: FluidMenuState;
  menuItem?: FluidMenuItem;
  submenuDisabled?: boolean;
  i18nStore?: FluidI18nStore;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Renders Header's main navigation via "links" Prop as a series of
 * links, submenus, and/or mega menus.
 */
export const HeaderNavigation: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, setRef, i18nStore } = props;

  return (
    !!headerState?.menuItems?.length && (
      <Nav
        aria-label='Main'
        ref={(el: HTMLElement) => setRef(el)}
        class={`header-nav align-${host.alignment}`}>
        {headerState.menuItems.map((item: FluidMenuItem) => (
          <HeaderMenuItem
            host={host}
            i18nStore={i18nStore}
            headerState={headerState}
            menuItem={item}
          />
        ))}
      </Nav>
    )
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * Renders <li> container for menu item's content.
 */
const HeaderMenuItem: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, menuItem, i18nStore } = props;
  const hoverSupport =
    hoverSupported(host) &&
    (hasHeaderSubmenu(menuItem) || hasMegaMenu(menuItem)) &&
    !isChildLink(headerState, menuItem);

  return (
    <NavItem
      onMouseEnter={
        hoverSupport &&
        (() => {
          host.toggleSubmenu(menuItem, true);
          menuItem.submenuOpen && Utils.setFocus(menuItem._submenuElement);
        })
      }
      onMouseLeave={
        hoverSupport &&
        (() => {
          host.toggleSubmenu(menuItem, false);
        })
      }
      class={{
        'header-nav-item': true,
        'has-submenu': hasHeaderSubmenu(menuItem),
        'has-mega-menu': hasMegaMenu(menuItem),
      }}>
      <HeaderMenuItemContent {...props} i18nStore={i18nStore} />
    </NavItem>
  );
};

/**
 * Renders a FluidMenuItem object as a standard link, submenu,
 * or mega menu.
 */
const HeaderMenuItemContent: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { menuItem } = props;

  switch (true) {
    case hasSubmenu(menuItem):
      return <HeaderSubmenu {...props} />;
    case hasMegaMenu(menuItem):
      return <HeaderMegaMenu {...props} />;
    default:
      return <HeaderLink {...props} />;
  }
};

/**
 * Renders a standard Header link (i.e. <a> or <button> element).
 * If link is child of submenu or mega menu column, receives
 * "disabled" status from parent menu item.
 */
const HeaderLink: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, menuItem, submenuDisabled, i18nStore } = props;
  const hoverSupport =
    hoverSupported(host) && !isChildLink(headerState, menuItem);
  const childLink = isChildLink(headerState, menuItem);
  !!submenuDisabled && (menuItem.disabled = true);

  return (
    <NavButton
      menuItem={menuItem}
      i18nStore={i18nStore}
      theme={host.theme}
      onClick={(event) => host.handleLinkClick(menuItem, event)}
      onFocus={hoverSupport && (() => host.closeAllSubmenus())}
      class={{
        'header-link': !childLink,
        'submenu-link': childLink,
      }}
    />
  );
};

/**
 * Renders "toggle button" (i.e. "submenu trigger" element) for
 * submenu or mega menu.
 */
const HeaderSubmenuBtn: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, menuItem, i18nStore } = props;

  return (
    <NavSubmenuBtn
      menuItem={menuItem}
      i18nStore={i18nStore}
      theme={host.theme}
      dropdownIcon={true}
      ref={(el) => (menuItem._submenuTrigger = el as HTMLElement)}
      onClick={() => host.toggleSubmenu(menuItem)}
      onFocus={
        hoverSupported(host) && (() => host.toggleSubmenu(menuItem, true))
      }
      class={{
        'header-submenu-button': hasSubmenu(menuItem),
        'header-mega-menu-button': hasMegaMenu(menuItem),
        'selected-item-parent': menuItem === headerState.selectedItemParent,
      }}
    />
  );
};

/**
 * Renders "submenu toggle button", submenu container, & nested
 * submenu links.
 */
const HeaderSubmenu: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, menuItem, i18nStore } = props;

  return [
    <HeaderSubmenuBtn {...props} />,
    <NavSubmenu
      menuItem={menuItem}
      tabIndex={-1}
      ref={(el) => (menuItem._submenuElement = el as HTMLElement)}
      onMouseDown={(event) => Utils.preventFocus(event)}
      class={{
        'header-submenu': true,
      }}>
      {menuItem.submenu.map((item: FluidMenuItem) => (
        <HeaderMenuItem
          host={host}
          i18nStore={i18nStore}
          headerState={headerState}
          menuItem={item}
          submenuDisabled={menuItem.disabled}
        />
      ))}
    </NavSubmenu>,
  ];
};

/**
 * Renders a "submenu toggle button", mega menu container, menu
 * title (optional), & embedded links (columns or cascading list).
 */
const HeaderMegaMenu: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { menuItem, i18nStore } = props;

  return [
    <HeaderSubmenuBtn {...props} i18nStore={i18nStore} />,
    <div
      tabIndex={-1}
      ref={(el) => (menuItem._submenuElement = el as HTMLElement)}
      onMouseDown={(event) => Utils.preventFocus(event)}
      class={{
        'header-mega-menu': true,
        'mega-menu-open': menuItem.submenuOpen,
        'mega-menu-closed': !menuItem.submenuOpen,
      }}>
      <HeaderMegaMenuPanel {...props} />
      <HeaderMegaMenuContent {...props} i18nStore={i18nStore} />
    </div>,
  ];
};

/**
 * Renders a left-hand panel within mega menu containing a menu title,
 * dynamic slot, or both. Appears side-by-side with HeaderMegaMenuContent.
 * TODO: support for dynamic slots (i.e. "slotName" property)
 * TODO: see https://libertymutual.atlassian.net/browse/FLUID-503
 */
const HeaderMegaMenuPanel: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { menuItem } = props;
  const title = menuItem.megaMenu.find((item) => !!item.sectionTitle)?.label;
  const slotName = menuItem.megaMenu.find((item) => !!item.slotName)?.slotName;

  return (
    (!!title || !!slotName) && (
      <div class='mega-menu-panel'>
        {!!title && <span class='mega-menu-title'>{title}</span>}
        {!!slotName && <slot name={slotName} />}
      </div>
    )
  );
};

/**
 * Renders mega menu content container & initiates loop to render
 * nested link columns or list of unstructured links.
 */
const HeaderMegaMenuContent: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps
) => {
  const { host, headerState, menuItem, i18nStore } = props;
  const megaMenuItems = menuItem.megaMenu.filter(
    (item) => !item.sectionTitle && !item.slotName
  );
  const columns = megaMenuItems.filter((menuItem: FluidMenuItem) =>
    hasHeaderSubmenu(menuItem)
  );
  const links = megaMenuItems.filter(
    (menuItem: FluidMenuItem) => !hasHeaderSubmenu(menuItem)
  );

  const maxColumnLength = getMaxColumnLength(host, columns);
  const columnCount = getColumnCount(host, columns, links, maxColumnLength);

  return (
    <div class='mega-menu-content'>
      {!!columns.length &&
        columns.map((item: FluidMenuItem) => (
          <HeaderMegaMenuColumn
            host={host}
            i18nStore={i18nStore}
            headerState={headerState}
            menuItem={item}
            columnCount={columnCount}
          />
        ))}
      {!!links.length && (
        <ul
          class='mega-menu-submenu grid-layout'
          style={
            maxColumnLength > megaMenuColumnLength && {
              gridTemplateRows: `repeat(${maxColumnLength}, auto)`,
            }
          }>
          {links.map((item: FluidMenuItem) => (
            <HeaderMenuItem
              i18nStore={i18nStore}
              host={host}
              headerState={headerState}
              menuItem={item}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Renders a single column of links with optional title to be displayed
 * within a mega menu.
 */
const HeaderMegaMenuColumn: FunctionalComponent<HeaderNavProps> = (
  props: HeaderNavProps & { columnCount?: number }
) => {
  const { host, headerState, menuItem, columnCount, i18nStore } = props;

  return (
    <div class={`mega-menu-column mega-menu-col-${columnCount}`}>
      {!!menuItem.label && (
        <div class='mega-menu-column-title'>
          <span class='column-title-text'>
            {i18nStore?.translate(menuItem.fluidTranslate?.label) ||
              menuItem.label}
          </span>
        </div>
      )}
      <ul class='mega-menu-submenu'>
        {menuItem.submenu.map((item: FluidMenuItem) => (
          <HeaderMenuItem
            i18nStore={i18nStore}
            host={host}
            headerState={headerState}
            menuItem={item}
            submenuDisabled={menuItem.disabled}
          />
        ))}
      </ul>
    </div>
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Returns true if device & viewport support :hover events AND
 * "hoverOpenSubmenus" Prop is true.
 */
const hoverSupported = (host: FluidHeader): boolean => {
  return host.hoverOpenSubmenus && !host._mobileViewport && !host._touchDevice;
};

/**
 * Returns true if given menu item is nested within a submenu / mega
 * menu, false if menu item appears in Header's main container (i.e.
 * is a "top-level" link).
 * @param headerState - Header's derived state object
 * @param menuItem - the menu item to check
 */
const isChildLink = (
  headerState: FluidMenuState,
  menuItem: FluidMenuItem
): boolean => {
  return !headerState.menuItems.includes(menuItem);
};

/**
 * Determines the largest column (i.e. has the most links) in a mega menu.
 * Given an array of FluidMenuItem objects that each contain a submenu
 * (extracted from "megaMenu" array), returns the number of links from
 * the largest mega menu submenu (i.e. "column"). Also validates that
 * the largest column doesn't contain more links than the recommended max.
 * @param host - FluidHeader class instance (for console warning)
 * @param columns - array of submenus extracted from a FluidMenuItem's "megaMenu" property
 */
const getMaxColumnLength = (
  host: FluidHeader,
  columns: FluidMenuItem[]
): number => {
  if (!columns?.length) return;

  return columns.reduce((max: number, col: FluidMenuItem) => {
    const submenuLength = col.submenu?.length;
    submenuLength > megaMenuMaxColumnLength &&
      Utils.consoleWarn(
        host.host,
        MEGA_MENU_COLUMN_LENGTH(col.label, megaMenuMaxColumnLength)
      );
    return submenuLength > max ? submenuLength : max;
  }, 0);
};

/**
 * Counts the total number of columns displayed in a mega menu - this
 * includes explicitly defined columns (i.e. "submenu" property) AND
 * implicit columns created via "unstructured" links. Used to set max
 * widths on a mega menu's columns based on the total number of columns
 * that exist. Also validates that the columns displayed are within the
 * recommended minimum/maximum range.
 * @param host - FluidHeader class instance (for console warning)
 * @param columns - array of submenus extracted from a FluidMenuItem's "megaMenu" property
 * @param links - array of "unstructured" links extracted from a FluidMenuItem's "megaMenu" property
 * @param maxColLength - the number of links from the largest mega menu column
 */
const getColumnCount = (
  host: FluidHeader,
  columns: FluidMenuItem[],
  links: FluidMenuItem[],
  maxColLength: number = megaMenuColumnLength
) => {
  const colCount = columns?.length;
  const linkColCount = Math.ceil(links?.length / maxColLength);
  const totalCols = colCount + linkColCount;

  totalCols < megaMenuMinColumns &&
    Utils.consoleWarn(host.host, MEGA_MENU_MIN_COLUMNS(megaMenuMinColumns));
  totalCols > megaMenuMaxColumns &&
    Utils.consoleWarn(host.host, MEGA_MENU_MAX_COLUMNS(megaMenuMaxColumns));

  return totalCols;
};
